/**
 * Email API Contracts
 * Zod schemas for email-related API validation
 */

import { z } from "zod";

/**
 * Email type enum schema
 */
export const EmailTypeSchema = z.enum([
  "welcome",
  "password_reset",
  "profile_approved",
  "profile_waitlisted",
  "profile_rejected",
  "appeal_requested",
  "appeal_approved",
  "appeal_rejected",
  "job_alert",
]);

/**
 * Email recipient schema
 */
export const EmailRecipientSchema = z.object({
  email: z.string().email(),
  userId: z.string().min(1),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  name: z.string().optional(),
});

/**
 * Welcome email data schema
 */
export const WelcomeEmailDataSchema = z.object({
  signupDate: z.string(),
});

/**
 * Password reset email data schema
 */
export const PasswordResetEmailDataSchema = z.object({
  resetToken: z.string().min(1),
  resetUrl: z.string().url(),
  expiresAt: z.string(),
});

/**
 * Profile approved email data schema
 */
export const ProfileApprovedEmailDataSchema = z.object({
  niches: z.array(z.string()),
  jobCount: z.number().optional(),
});

/**
 * Profile waitlisted email data schema
 */
export const ProfileWaitlistedEmailDataSchema = z.object({
  reason: z.string(),
  niches: z.array(z.string()),
});

/**
 * Profile rejected email data schema
 */
export const ProfileRejectedEmailDataSchema = z.object({
  reason: z.string(),
  rejectionCode: z.string(),
});

/**
 * Appeal requested email data schema
 */
export const AppealRequestedEmailDataSchema = z.object({
  appealRequestedAt: z.string(),
  originalRejectionReason: z.string().optional(),
});

/**
 * Appeal result email data schema
 */
export const AppealResultEmailDataSchema = z.object({
  approved: z.boolean(),
  reason: z.string().optional(),
  processedAt: z.string(),
});

/**
 * Job in job alert schema
 */
export const JobAlertJobSchema = z.object({
  title: z.string(),
  company: z.string(),
  location: z.string().optional(),
  salary: z.string().optional(),
  url: z.string().url(),
});

/**
 * Job alert email data schema
 */
export const JobAlertEmailDataSchema = z.object({
  jobs: z.array(JobAlertJobSchema),
  totalMatches: z.number(),
});

/**
 * Send email request schema
 */
export const SendEmailRequestSchema = z.object({
  type: EmailTypeSchema,
  recipient: EmailRecipientSchema,
  data: z.record(z.string(), z.unknown()),
});

/**
 * Send email response schema
 */
export const SendEmailResponseSchema = z.object({
  success: z.boolean(),
  messageId: z.string().optional(),
  error: z.string().optional(),
});

// Type exports
export type EmailTypeDto = z.infer<typeof EmailTypeSchema>;
export type EmailRecipientDto = z.infer<typeof EmailRecipientSchema>;
export type WelcomeEmailDataDto = z.infer<typeof WelcomeEmailDataSchema>;
export type PasswordResetEmailDataDto = z.infer<typeof PasswordResetEmailDataSchema>;
export type ProfileApprovedEmailDataDto = z.infer<typeof ProfileApprovedEmailDataSchema>;
export type ProfileWaitlistedEmailDataDto = z.infer<typeof ProfileWaitlistedEmailDataSchema>;
export type ProfileRejectedEmailDataDto = z.infer<typeof ProfileRejectedEmailDataSchema>;
export type AppealRequestedEmailDataDto = z.infer<typeof AppealRequestedEmailDataSchema>;
export type AppealResultEmailDataDto = z.infer<typeof AppealResultEmailDataSchema>;
export type JobAlertEmailDataDto = z.infer<typeof JobAlertEmailDataSchema>;
export type SendEmailRequestDto = z.infer<typeof SendEmailRequestSchema>;
export type SendEmailResponseDto = z.infer<typeof SendEmailResponseSchema>;
